import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ListingContext from '../../../../../../providers/Listing/Listing.context';
import {
  getArrayLength,
  getDataRoomRoute,
  getHasElementsInArray,
  sentryError,
} from 'utils';
import api from 'api';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import { Link } from 'react-router-dom';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { getDocumentOptions } from './utils';
import TransWithNda from 'components/TransWithNda';

import './AddCustomNdaModal.styles.scss';

const INPUT_NAME = 'documents';

type AddCustomNdaModalProps = {
  className?: string;
  envelopeTemplateName: string;
};

const AddCustomNdaModal: ForwardRefRenderFunction<
  OverlayRef,
  AddCustomNdaModalProps
> = (props, ref) => {
  const { className, envelopeTemplateName } = props;

  const { popSuccess } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { hasUnlockPremiumFeatures, listing, setListing } =
    useContext(ListingContext);

  const {
    _id: listingId,
    dataRoomDocumentsAvailabilityAfterSignNDA,
    dataRoomId,
  } = listing || {};

  const classes = classNames('add-custom-nda-modal', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const hasDocumentsToUnlockAfterNDA = getHasElementsInArray(
    dataRoomDocumentsAvailabilityAfterSignNDA,
  );

  const documentOptions = useMemo(
    () =>
      hasDocumentsToUnlockAfterNDA
        ? getDocumentOptions(dataRoomDocumentsAvailabilityAfterSignNDA)
        : [],
    [dataRoomDocumentsAvailabilityAfterSignNDA, hasDocumentsToUnlockAfterNDA],
  );

  const initialValues = useMemo(() => {
    if (!hasDocumentsToUnlockAfterNDA) return { documents: {} };

    const documents: Record<string, boolean> = {};

    (dataRoomDocumentsAvailabilityAfterSignNDA || [])?.forEach(
      ({ fileId, checked }) => (documents[fileId] = checked),
    );

    return {
      documents,
    };
  }, [dataRoomDocumentsAvailabilityAfterSignNDA, hasDocumentsToUnlockAfterNDA]);

  const changeForAll = useCallback(
    (isChecked: boolean) =>
      documentOptions.reduce(
        (acc, { value }) => ({ ...acc, [value]: isChecked }),
        {},
      ),
    [documentOptions],
  );

  const handleDocumentsAfterNDA = useCallback(
    async (values) => {
      const { documents } = values;

      const parsedDocuments: any = [];

      Object.entries(documents || {}).forEach(([key, value]) => {
        if (value) parsedDocuments.push(key);
      });

      try {
        const { data } = await api.portfolio.seller.documentsToUnlockAfterNDA(
          listingId,
          parsedDocuments,
        );
        popSuccess(t('successfullyAppliedChanges'));
        setListing(data);
        modal.close();
      } catch (error) {
        sentryError(error);
      }
    },
    [modal, listingId, popSuccess, setListing, t],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t(
        hasDocumentsToUnlockAfterNDA
          ? 'chooseDocuments'
          : 'noDocumentsUploaded',
      )}
    >
      <Form
        onSubmit={handleDocumentsAfterNDA}
        initialValues={initialValues}
        render={({
          pristine,
          submitting,
          values: { [INPUT_NAME]: documentsValue },
          form: { change },
          handleSubmit,
        }) => {
          const documentsValueArray = Object.values(documentsValue || {});

          const isSelectedAll =
            getHasElementsInArray(documentsValueArray) &&
            getArrayLength(documentsValueArray) ===
              getArrayLength(documentOptions) &&
            documentsValueArray.every(Boolean);

          return (
            <form onSubmit={handleSubmit}>
              {hasDocumentsToUnlockAfterNDA ? (
                <>
                  <p>
                    <span>{t('documentsUnlockAfterNdaDescription')}</span>
                    {isSuperAdmin || hasUnlockPremiumFeatures ? (
                      <Link to={getDataRoomRoute(dataRoomId)}>Data Room</Link>
                    ) : (
                      'Data Room'
                    )}
                    {'. '}

                    <TransWithNda
                      i18nKey="documentsUnlockAfterNdaDescription2"
                      envelopeTemplateName={envelopeTemplateName}
                    />
                  </p>

                  <Checkbox
                    className="select-all-checkbox"
                    checked={isSelectedAll}
                    label={t('selectAll')}
                    onChange={() =>
                      change(INPUT_NAME, changeForAll(!isSelectedAll))
                    }
                  />

                  <CheckboxFieldGroup
                    name={INPUT_NAME}
                    options={documentOptions}
                  />
                </>
              ) : (
                <p>
                  <TransWithNda
                    i18nKey="noDocumentsFirstUpload"
                    envelopeTemplateName={envelopeTemplateName}
                  />
                </p>
              )}

              <div className="actions">
                <Button variant="link" onClick={modal?.close}>
                  {t('cancel')}
                </Button>
                {hasDocumentsToUnlockAfterNDA && (
                  <Button type="submit" disabled={pristine || submitting}>
                    {t('confirm')}
                  </Button>
                )}
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(AddCustomNdaModal);
